import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LinkIcon from "@mui/icons-material/Link";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import SysRecord from "app/class/record";
import SysColumn from "app/class/sys-column";
import { RecordManagement } from "app/class/record-management";
import { useColumns } from "app/hooks/useColumns";
import { useTables } from "app/hooks/useTables";
import queryKeys from "shared/services/queryKeys";
import { recordMutationBus } from "shared/services/recordMutationBus";

import { BridgeRecord, ResolvedRelatedList } from "../types";
import Translate from "component/global/translation/translation-component";
import { useTranslation } from "react-i18next";
import useNotification from "component/global/event-handler/hooks/use-notification";

interface RelatedListRelationshipEditorProps {
    open: boolean;
    relatedList: ResolvedRelatedList;
    routeId: string;
    bridgeRecords: BridgeRecord[];
    onClose: () => void;
    onSaved: () => Promise<void> | void;
}

interface TargetOption {
    id: string;
    label: string;
    description: string;
    record: SysRecord;
}

const COMMON_DISPLAY_FIELDS = [
    "display_value",
    "display",
    "label",
    "name",
    "title",
    "subject",
    "email",
    "email_address",
    "username",
    "code",
    "number_ref",
];

const valueToDisplay = (value: unknown) => {
    if (value == null || value === "") return "";

    if (typeof value === "object" && !Array.isArray(value)) {
        const candidate = value as Record<string, unknown>;
        return String(candidate.display || candidate.label || candidate.name || candidate.number_ref || candidate.code || "");
    }

    return typeof value === "boolean" ? "" : String(value);
};

const firstDisplayValue = (record: SysRecord, fields: string[]) => {
    for (const field of fields) {
        const value = valueToDisplay(record?.[field]);
        if (value) return value;
    }

    return "";
};

const buildRecordLabel = (record: SysRecord, displayColumns: SysColumn[]) => {
    const displayFields = [
        ...displayColumns.map((column) => column.name),
        ...COMMON_DISPLAY_FIELDS,
    ];
    return firstDisplayValue(record, displayFields);
};

const buildRecordDescription = (record: SysRecord, columns: SysColumn[]) => {
    const candidateColumns = columns
        .filter((column) => !column.is_hidden && !["id", "created_at", "updated_at"].includes(column.name))
        .sort((a, b) => a.order - b.order)
        .slice(0, 3);

    return candidateColumns
        .map((column) => valueToDisplay(record?.[column.name]))
        .filter(Boolean)
        .join(" • ");
};

export const RelationshipOptionRow = memo(({ option, checked, onToggle }: {
    option: TargetOption;
    checked: boolean;
    onToggle: (id: string) => void;
}) => {
    const secondary = option.description.split(/\s*[•·]\s*/)
        .filter((part) => part !== "true" && part !== "false" && part.trim() !== option.label.trim())
        .join(" · ") || "No additional details";
    return <ListItemButton
        data-relationship-option-id={option.id}
        role="checkbox"
        aria-checked={checked}
        onClick={() => onToggle(option.id)}
        sx={{
            alignItems: "flex-start", gap: 0.5, minHeight: { xs: 56, sm: 52 },
            borderBottom: "1px solid", borderColor: "divider",
            bgcolor: checked ? "var(--prm-selected-bg)" : "transparent",
            color: checked ? "var(--prm-text-on-selected)" : "text.primary",
            "&:hover": { bgcolor: checked ? "var(--prm-selected-bg)" : "action.hover" },
        }}
    >
        <ListItemIcon sx={{ minWidth: 36 }}>
            <Checkbox edge="start" checked={checked} tabIndex={-1} aria-hidden disableRipple color="primary" />
        </ListItemIcon>
        <ListItemText primary={option.label} secondary={secondary}
            primaryTypographyProps={{ variant: "body2", fontWeight: 700 }}
            secondaryTypographyProps={{ variant: "caption", noWrap: true, color: checked ? "var(--prm-text-on-selected)" : "text.secondary" }} />
    </ListItemButton>;
});

const RelatedListRelationshipEditor = ({
    open,
    relatedList,
    routeId,
    bridgeRecords,
    onClose,
    onSaved,
}: RelatedListRelationshipEditorProps) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const notifyApi = useNotification();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { data: columns = [] } = useColumns();
    const { data: tables = [] } = useTables();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [availableSearch, setAvailableSearch] = useState("");
    const [selectedSearch, setSelectedSearch] = useState("");
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const pendingFocusId = useRef<string | null>(null);
    const dialogRef = useRef<HTMLDivElement | null>(null);
    const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    useLayoutEffect(() => {
        const id = pendingFocusId.current;
        if (!id || !dialogRef.current) return;
        const target = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("[data-relationship-option-id]"))
            .find((node) => node.dataset.relationshipOptionId === id);
        if (target) {
            target.focus();
            pendingFocusId.current = null;
        }
    }, [selectedIds]);

    const targetTable = useMemo(
        () => tables.find((table) => table.name === relatedList.tableName),
        [relatedList.tableName, tables]
    );

    const targetColumns = useMemo(
        () => columns
            .filter((column) => column.sys_table_id === targetTable?.id)
            .sort((a, b) => a.order - b.order),
        [columns, targetTable?.id]
    );

    const displayColumns = useMemo(
        () => targetColumns.filter((column) => column.is_display),
        [targetColumns]
    );

    const selectedTargetIds = useMemo(
        () => bridgeRecords.map((record) => String(record.targetId)),
        [bridgeRecords]
    );

    useEffect(() => {
        if (!open) {
            return;
        }

        setSelectedIds(selectedTargetIds);
        setAvailableSearch("");
        setSelectedSearch("");
        setConfirmDiscard(false);
    }, [open, selectedTargetIds]);

    const hasPendingSelection = selectedIds.length !== selectedTargetIds.length ||
        selectedIds.some((id) => !selectedTargetIds.includes(id));
    const requestClose = () => {
        if (hasPendingSelection) {
            setConfirmDiscard(true);
            return;
        }
        onClose();
    };

    const { data: options = [], isLoading } = useQuery({
        queryKey: queryKeys.records.list(relatedList.tableName, "editor", {
            relatedList: relatedList.definition.id,
            mode: "relationship_editor",
        }),
        queryFn: async () => {
            const response = await new RecordManagement<SysRecord>(relatedList.tableName)
                .referenceMode("display")
                .run();

            return response.data.map((record) => ({
                id: String(record.id),
                label: buildRecordLabel(record, displayColumns) || String(t("Record without display value")),
                description: buildRecordDescription(record, targetColumns),
                record,
            })) as TargetOption[];
        },
        enabled: open && targetColumns.length > 0,
    });

    const availableOptions = useMemo(() => {
        return options.filter((option) => !selectedIdSet.has(option.id));
    }, [options, selectedIdSet]);

    const chosenOptions = useMemo(() => {
        return options.filter((option) => selectedIdSet.has(option.id));
    }, [options, selectedIdSet]);

    const filterOptions = (items: TargetOption[], search: string) => {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) return items;

        return items.filter((option) =>
            `${option.label} ${option.description}`.toLowerCase().includes(normalizedSearch)
        );
    };

    const filteredAvailableOptions = useMemo(
        () => filterOptions(availableOptions, availableSearch),
        [availableOptions, availableSearch]
    );

    const filteredChosenOptions = useMemo(
        () => filterOptions(chosenOptions, selectedSearch),
        [chosenOptions, selectedSearch]
    );

    const { mutateAsync, isPending } = useMutation({
        mutationFn: async () => {
            const bridge = relatedList.bridge;
            if (!bridge?.tableName || !bridge.sourceColumnName || !bridge.targetColumnName) {
                return;
            }

            const currentIdSet = new Set(selectedTargetIds);
            const nextIdSet = new Set(selectedIds);

            const idsToCreate = selectedIds.filter((id) => !currentIdSet.has(id));
            const bridgeRecordsToDelete = bridgeRecords.filter(
                (record) => !nextIdSet.has(String(record.targetId))
            );

            const bridgeManager = new RecordManagement(bridge.tableName);

            await Promise.all(
                idsToCreate.map((targetId) =>
                    bridgeManager.save({
                        [bridge.sourceColumnName]: routeId,
                        [bridge.targetColumnName]: targetId,
                    } as any)
                )
            );

            await Promise.all(
                bridgeRecordsToDelete
                    .filter((record) => record.id)
                    .map((record) => bridgeManager.delete(String(record.id)))
            );
        },
        onSuccess: async () => {
            if (relatedList.bridge?.tableName) {
                recordMutationBus.emit({ table: relatedList.bridge.tableName });
            }
            recordMutationBus.emit({ table: relatedList.tableName });
            await onSaved();
            onClose();
        },
    });

    const toggleSelection = useCallback((targetId: string) => {
        pendingFocusId.current = targetId;
        setSelectedIds((current) =>
            current.includes(targetId)
                ? current.filter((id) => id !== targetId)
                : [...current, targetId]
        );
    }, []);

    const moveAllToSelected = () => {
        setSelectedIds(options.map((option) => option.id));
    };

    const clearSelection = () => {
        setSelectedIds([]);
    };

    const saveChanges = async () => {
        try {
            await mutateAsync();
            await queryClient.invalidateQueries({
                queryKey: queryKeys.records.all(relatedList.tableName),
                exact: false,
            });
            notifyApi.success("relationships-save-success", String(t("Relationships updated successfully!")));
        } catch (_error) {
            notifyApi.alert("relationships-save-error", String(t("Unable to update relationships.")));
        }
    };

    const renderOptionList = (
        title: string,
        items: TargetOption[],
        totalItems: number,
        search: string,
        onSearchChange: (value: string) => void
    ) => (
        <Paper
            variant="outlined"
            sx={{
                width: "100%",
                minHeight: { xs: 260, md: 420 },
                overflow: "hidden",
                borderRadius: 2,
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box
                sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    bgcolor: "action.hover",
                }}
            >
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Typography variant="subtitle2" fontWeight={700}>{t(title)}</Typography>
                    <Chip
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={t("record.related_list.items_count", { count: totalItems })}
                        sx={{ bgcolor: "var(--prm-tint-bg)" }}
                    />
                </Stack>
                <TextField
                    size="small"
                    fullWidth
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={String(t("Search records"))}
                    sx={{ mt: 1.25 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            <Divider />
            <List dense sx={{ flex: 1, minHeight: 0, maxHeight: { xs: "min(28vh, 240px)", md: "min(50vh, 520px)" }, overflow: "auto", py: 0 }}>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("No records found")}
                        </Typography>
                    </Box>
                ) : items.map((option) => <RelationshipOptionRow key={option.id} option={option}
                    checked={selectedIdSet.has(option.id)} onToggle={toggleSelection} />)}
            </List>
        </Paper>
    );

    return (
        <>
        <Dialog
            ref={dialogRef}
            open={open}
            onClose={requestClose}
            fullWidth
            maxWidth="lg"
            fullScreen={fullScreen}
            PaperProps={{
                sx: {
                    bgcolor: "background.paper",
                    color: "text.primary",
                },
            }}
        >
            <DialogTitle
                sx={{
                    pb: 1.5,
                    bgcolor: "var(--prm-tint-bg)",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: "var(--prm-selected-bg)",
                            color: "var(--prm-accent-on-tint)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <LinkIcon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700}>
                            {t("Edit relationships")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("Choose the records that should remain related.")}
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{
                    bgcolor: "background.default",
                    p: { xs: 1.5, sm: 2.5 },
                    borderColor: "divider",
                    overflowY: { xs: "auto", md: "hidden" },
                }}
            >
                <Stack spacing={2}>
                    <Stack data-testid="relationship-header-actions" direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between"
                        sx={{ position: "sticky", top: 0, zIndex: 1, bgcolor: "background.default" }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                            size="small"
                            color="primary"
                            variant="outlined"
                            label={`${selectedIds.length} ${t("selected")}`}
                            sx={{ alignSelf: { xs: "flex-start", sm: "center" }, bgcolor: "var(--prm-tint-bg)" }}
                        />
                        <Button size="small" variant="outlined" onClick={moveAllToSelected} disabled={isLoading || isPending}><Translate text="Select all" /></Button>
                        <Button size="small" variant="outlined" color="inherit" onClick={clearSelection} disabled={isLoading || isPending}><Translate text="Clear all" /></Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {t(String(relatedList.definition.name || relatedList.tableName))}
                        </Typography>
                    </Stack>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
                        <Box data-testid="relationship-pane" sx={{ flex: "1 1 0", minWidth: 0 }}>
                            {renderOptionList("Available records", filteredAvailableOptions, availableOptions.length, availableSearch, setAvailableSearch)}
                        </Box>
                        <Box data-testid="relationship-pane" sx={{ flex: "1 1 0", minWidth: 0 }}>
                            {renderOptionList("Selected records", filteredChosenOptions, chosenOptions.length, selectedSearch, setSelectedSearch)}
                        </Box>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5, flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1 }}>
                <Button variant="outlined" color="inherit" onClick={requestClose} disabled={isPending}>
                    <Translate text="Cancel" />
                </Button>
                <Button variant="contained" color="primary" onClick={saveChanges} disabled={isLoading || isPending} fullWidth={fullScreen}>
                    {isPending ? <Translate text="Saving..." /> : <Translate text="Save" />}
                </Button>
            </DialogActions>
        </Dialog>
        <Dialog open={open && confirmDiscard} onClose={() => setConfirmDiscard(false)} aria-labelledby="discard-relationships-title">
            <DialogTitle id="discard-relationships-title">{t("Discard changes?")}</DialogTitle>
            <DialogContent>{t("You have unsaved changes. Discard them?")}</DialogContent>
            <DialogActions>
                <Button autoFocus variant="outlined" color="inherit" onClick={() => setConfirmDiscard(false)}>{t("Keep editing")}</Button>
                <Button variant="contained" onClick={() => { setConfirmDiscard(false); onClose(); }}>{t("Discard")}</Button>
            </DialogActions>
        </Dialog>
        </>
    );
};

export default RelatedListRelationshipEditor;
